import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { addDays } from 'date-fns'
import { DatetimePickerComponent } from '../../common/datetime-picker/datetime-picker.component'
import { resolveLocalTimeZone } from '../../utils'

/** How far out the dialog offers to push a meeting whose dates have already passed. */
const DEFAULT_EXTENSION_IN_DAYS = 30

export type UndoSoftDeletionDialogData = {
  callId: number
  /** Where the meeting's two deletion dates stand now; both may have passed, which is why it is hidden. */
  currentSoftDeletionDate: Date | null
  currentDeletionDate: Date | null
}

export type UndoSoftDeletionDialogResult = {
  /** Null to bring the meeting back without moving either of its deletion dates. */
  dates: { softDeletionDate: Date; deletionDate: Date } | null
}

@Component({
  selector: 'app-undo-soft-deletion-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatCheckboxModule,
    TranslatePipe,
    TranslateDirective,
    MatDialogTitle,
    DatetimePickerComponent,
  ],
  templateUrl: './undo-soft-deletion-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './undo-soft-deletion-dialog.component.scss',
})
export class UndoSoftDeletionDialogComponent {
  data: UndoSoftDeletionDialogData = inject(MAT_DIALOG_DATA)

  // The picker reads and writes a plain Date, so the date is in the user's own time zone. Naming it
  // keeps it from being read as the British time the meeting screens show elsewhere.
  localTimeZone = resolveLocalTimeZone()

  private currentSoftDeletionDate = this.data.currentSoftDeletionDate === null ? null : new Date(this.data.currentSoftDeletionDate)
  private currentDeletionDate = this.data.currentDeletionDate === null ? null : new Date(this.data.currentDeletionDate)

  // Bringing a meeting back whose soft deletion has already passed would drop it straight into the
  // next deletion run, so the dates have to move as part of the same action rather than being
  // offered as an extra.
  mustMoveDeletionDates = this.currentSoftDeletionDate !== null && this.currentSoftDeletionDate.getTime() <= Date.now()

  // A meeting its organization keeps forever has no date for the retention caps to be measured from,
  // so the server refuses to give it any.
  canMoveDeletionDates = this.currentSoftDeletionDate !== null || this.currentDeletionDate !== null

  moveDeletionDates = this.mustMoveDeletionDates

  // Defaulted a month out from today when the dates are being forced, since the ones the meeting has
  // are behind it and would leave it due to be hidden again at once. The deletion date is held to the
  // soft-deletion date it ends up with, or a meeting due to be deleted sooner than a month from now
  // would open on a pair the dialog then refuses to save.
  softDeletionDate: Date = this.defaultFor(this.currentSoftDeletionDate)
  deletionDate: Date = this.laterOf(this.defaultFor(this.currentDeletionDate), this.defaultFor(this.currentSoftDeletionDate))

  private defaultFor(current: Date | null): Date {
    const now = new Date()
    return current !== null && current > now ? current : addDays(now, DEFAULT_EXTENSION_IN_DAYS)
  }

  private laterOf(first: Date, second: Date): Date {
    return first > second ? first : second
  }

  /** Only pushing the dates out restores a meeting; pulling them in is the opposite of what this is for. */
  get areDatesValid(): boolean {
    if (!this.moveDeletionDates) {
      return !this.mustMoveDeletionDates
    }
    if (this.deletionDate < this.softDeletionDate) {
      return false
    }
    // Leaving one of them where it is is allowed, as the server allows: an administrator may want to
    // move the other alone. What a forced move will not take is a soft deletion still behind the
    // meeting, since that is the very thing it is being restored from.
    if (this.mustMoveDeletionDates && this.softDeletionDate <= new Date()) {
      return false
    }
    const isSoftDeletionLater = this.currentSoftDeletionDate === null || this.softDeletionDate >= this.currentSoftDeletionDate
    const isDeletionLater = this.currentDeletionDate === null || this.deletionDate >= this.currentDeletionDate
    return isSoftDeletionLater && isDeletionLater
  }

  get dialogResult(): UndoSoftDeletionDialogResult {
    return {
      dates: this.moveDeletionDates ? { softDeletionDate: this.softDeletionDate, deletionDate: this.deletionDate } : null,
    }
  }
}
